import { useCallback, useEffect, useMemo, useState, useRef, type ReactNode } from "react";
import { useMutation } from "@tanstack/react-query";
import ky from "ky";
import { useAuthStore } from "../../state/auth";
import { Clock, History, Loader2, Send, Sparkles, User, FileText, ChevronDown } from "lucide-react";
import { cn } from "../../lib/utils";
import { useSearchParams } from "react-router-dom";
import { recordAiQuery, fetchRecentAiQueries, fetchCommonQuestions, type CommonQuestion } from "../../services/aiHistory";
import type { AiQueryRecord } from "../../lib/db";
import { useTranslation } from "../../i18n";

interface AskResponse {
  answer: string;
  sql: string;
  rows: Array<Record<string, unknown>>;
}

interface Message {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  sql?: string;
  rows?: Array<Record<string, unknown>>;
  timestamp: Date;
  /** Set when the message is a failure notice rather than an answer. */
  error?: boolean;
  /** Technical detail from the API (error text, generated SQL) for the "Details" disclosure. */
  detail?: string;
}

/**
 * Turn a failed /api/ask call into something the user can act on. The API
 * returns a JSON body of { error, detail?, sql? } with a meaningful HTTP
 * status; previously every failure — expired session, missing permission,
 * rate limit, AI outage, bad SQL — collapsed into one generic apology, so
 * nobody could tell what had actually gone wrong.
 */
async function describeAskFailure(
  error: any,
  t: (key: string, fallback?: string) => string
): Promise<{ content: string; detail?: string }> {
  const response: Response | undefined = error?.response;
  let body: any = null;
  if (response) {
    try { body = await response.clone().json(); } catch { body = null; }
  }
  const apiError: string | undefined = typeof body?.error === "string" ? body.error : undefined;
  const apiDetail: string | undefined = typeof body?.detail === "string" ? body.detail : undefined;
  const apiSql: string | undefined = typeof body?.sql === "string" ? body.sql : undefined;
  const status = response?.status;

  const detailParts = [status ? `HTTP ${status}` : null, apiError, apiDetail, apiSql ? `SQL: ${apiSql}` : null]
    .filter(Boolean) as string[];
  const detail = detailParts.length ? detailParts.join(" — ") : (error?.message ? String(error.message) : undefined);

  if (status === 401) {
    return {
      content: t("ask.errorSessionExpired", "Your session has expired, so I can't run that question. Please sign out and sign back in, then ask again."),
      detail
    };
  }
  if (status === 403) {
    return {
      content: t("ask.errorForbidden", "Your account doesn't have permission to use Ask AI. An administrator can grant access."),
      detail
    };
  }
  if (status === 429) {
    return {
      content: t("ask.errorRateLimited", "You've asked a lot of questions in the last minute. Please wait a moment and try again."),
      detail
    };
  }
  if (status === 504 || status === 408 || error?.name === "TimeoutError") {
    return {
      content: t("ask.errorTimeout", "That question took too long to answer. Try narrowing the date range or asking for a smaller breakdown."),
      detail
    };
  }
  if (!response) {
    return {
      content: t("ask.errorNetwork", "I couldn't reach the server. Check your connection and try again."),
      detail
    };
  }
  // Server-side failure (AI service, SQL). Show the API's own message when it has one.
  const base = t("ask.errorServer", "I wasn't able to answer that question.");
  return {
    content: apiError ? `${base} ${apiError}` : t("ask.errorMessage", "Sorry, I wasn't able to process that question. This could be due to a connection issue or an unsupported query. Please try rephrasing your question or try again in a moment."),
    detail
  };
}

/** "2:14 PM" today, "Sep 27, 2:14 PM" otherwise. */
function formatMessageTime(d: Date, lang: string): string {
  const now = new Date();
  const sameDay =
    d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth() && d.getDate() === now.getDate();
  return new Intl.DateTimeFormat(lang, sameDay
    ? { hour: 'numeric', minute: '2-digit' }
    : { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }
  ).format(d);
}

// ── Lightweight answer renderer ─────────────────────────────────────────────
// The AI answers in markdown-ish text (**bold**, bullet lists, newlines), but
// rendering it as a raw string showed literal asterisks and collapsed every
// newline into one long paragraph. This renders the three constructs the
// model actually uses — bold, bullets, line breaks — as React nodes (no
// dangerouslySetInnerHTML, no markdown dependency).

/** Inline pass: turn **bold** spans into <strong>. */
function renderInline(text: string, keyPrefix: string): ReactNode[] {
  const parts: ReactNode[] = [];
  const re = /\*\*(.+?)\*\*/g;
  let last = 0;
  let m: RegExpExecArray | null;
  let i = 0;
  while ((m = re.exec(text)) !== null) {
    if (m.index > last) parts.push(text.slice(last, m.index));
    parts.push(<strong key={`${keyPrefix}-b${i++}`} className="font-semibold">{m[1]}</strong>);
    last = m.index + m[0].length;
  }
  if (last < text.length) parts.push(text.slice(last));
  return parts;
}

/** Block pass: split into paragraphs and bullet lists. Handles bullets that
 *  arrive on their own lines (- / * / •) AND bullets the model inlined into a
 *  single line separated by " • ". */
function AnswerText({ text }: { text: string }) {
  const blocks: ReactNode[] = [];
  let bullets: string[] = [];
  let key = 0;

  const flushBullets = () => {
    if (!bullets.length) return;
    blocks.push(
      <ul key={`ul-${key++}`} className="my-1.5 ml-4 list-disc space-y-1">
        {bullets.map((b, i) => <li key={i}>{renderInline(b, `li-${key}-${i}`)}</li>)}
      </ul>
    );
    bullets = [];
  };

  for (const rawLine of text.split(/\r?\n/)) {
    const line = rawLine.trimEnd();
    const asBulletLine = line.match(/^\s*(?:[-*•])\s+(.*)$/);
    if (asBulletLine) {
      bullets.push(asBulletLine[1]);
      continue;
    }
    // Inline bullets: "intro: • one • two • three" on a single line.
    const inlineParts = line.split(/\s+•\s+/);
    if (inlineParts.length > 1) {
      const intro = inlineParts[0].replace(/[•]\s*$/, '').trim();
      if (intro) blocks.push(<p key={`p-${key++}`}>{renderInline(intro, `p-${key}`)}</p>);
      inlineParts.slice(1).forEach((b) => bullets.push(b.trim()));
      flushBullets();
      continue;
    }
    flushBullets();
    if (line.trim() === '') {
      blocks.push(<div key={`sp-${key++}`} className="h-2" />);
    } else {
      blocks.push(<p key={`p-${key++}`}>{renderInline(line, `p-${key}`)}</p>);
    }
  }
  flushBullets();
  return <>{blocks}</>;
}

export function AskPage() {
  const { t, lang } = useTranslation();

  const exampleQuestions = [
    t("ask.exampleInvalidScans", "Which locations logged the most INVALID scans this week?"),
    t("ask.exampleMissedScans", "Show me guards who missed 3 or more scans in the last 7 days."),
    t("ask.exampleOvernightScans", "How many scans were recorded overnight between 22:00 and 06:00?"),
    t("ask.exampleNoScansToday", "List locations that haven't received any scans today.")
  ];

  const user = useAuthStore((state) => state.user);
  const [question, setQuestion] = useState("");
  const [messages, setMessages] = useState<Message[]>([]);
  const [showSql, setShowSql] = useState<Record<string, boolean>>({});
  const [recentQueries, setRecentQueries] = useState<AiQueryRecord[]>([]);
  const [commonQuestions, setCommonQuestions] = useState<CommonQuestion[]>([]);
  const [historyLoading, setHistoryLoading] = useState(true);
  const [showHistory, setShowHistory] = useState(false);
  const [historySearch, setHistorySearch] = useState("");
  const [searchParams] = useSearchParams();
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const mutation = useMutation({
    mutationFn: async (payload: { question: string }) => {
      const { token, user: authUser } = useAuthStore.getState();
      const headers: Record<string, string> = {};
      if (token) {
        headers["Authorization"] = `Bearer ${token}`;
      }
      const csrfToken = localStorage.getItem("csrfToken");
      if (csrfToken) {
        headers["x-csrf-token"] = csrfToken;
      }
      if (authUser?.email) {
        headers["x-user-email"] = authUser.email;
      }
      // ky's default timeout is 10s. This request runs two model calls plus the
      // database query (and a Neon cold start on a quiet day), so it routinely
      // needs longer than that; a client-side abort here surfaced as the generic
      // "wasn't able to process that question" message. Match the server's
      // maxDuration instead.
      return ky.post("/api/ask", { json: payload, headers, timeout: 60_000, retry: 0 }).json<AskResponse>();
    },
    onSuccess: async (data, variables) => {
      const assistantMessage: Message = {
        id: Date.now().toString(),
        role: 'assistant',
        content: data.answer,
        sql: data.sql,
        rows: data.rows,
        timestamp: new Date()
      };

      setMessages(prev => [...prev, assistantMessage]);

      await recordAiQuery({
        question: variables.question,
        answer: data.answer,
        sql: data.sql,
        rowCount: data.rows.length
      });
      await loadHistory();
    },
    onError: async (error: any) => {
      console.error("Ask AI request failed", error);
      const { content, detail } = await describeAskFailure(error, t);
      const errorMessage: Message = {
        id: Date.now().toString(),
        role: 'assistant',
        content,
        detail,
        error: true,
        timestamp: new Date()
      };
      setMessages(prev => [...prev, errorMessage]);
    }
  });

  const loadHistory = useCallback(async () => {
    setHistoryLoading(true);
    try {
      const [recent, common] = await Promise.all([fetchRecentAiQueries(50), fetchCommonQuestions()]);
      setRecentQueries(recent);
      setCommonQuestions(common);
    } catch (error) {
      console.error("Failed to load AI history", error);
    } finally {
      setHistoryLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadHistory();
  }, [loadHistory]);

  const handleSubmit = (e?: React.FormEvent) => {
    e?.preventDefault();
    const q = question.trim();
    if (!q || mutation.isPending) return;

    const userMessage: Message = {
      id: Date.now().toString(),
      role: 'user',
      content: q,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setQuestion("");
    mutation.mutate({ question: q });
  };

  const handleSuggestionClick = (q: string) => {
    setQuestion(q);
    // Auto-submit suggestion
    const userMessage: Message = {
      id: Date.now().toString(),
      role: 'user',
      content: q,
      timestamp: new Date()
    };
    setMessages(prev => [...prev, userMessage]);
    setQuestion("");
    mutation.mutate({ question: q });
  };

  if (!user || (user.role !== 'ADMIN' && user.role !== 'Administrator')) {
    return (
      <div className="flex h-[50vh] flex-col items-center justify-center text-center">
        <h2 className="text-xl font-semibold text-gray-900">{t("ask.accessDenied", "Access Denied")}</h2>
        <p className="mt-2 text-gray-600">{t("ask.accessDeniedDesc", "The Ask AI feature is restricted to administrators.")}</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-[calc(100dvh-100px)] md:h-[calc(100vh-120px)] bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden relative">
      {/* Header */}
      <header className="flex items-center justify-between px-3 md:px-6 py-3 md:py-4 border-b border-gray-100 bg-gray-50/50">
        <div className="flex items-center gap-2">
          <div className="p-1 rounded-lg bg-white shadow-sm border border-gray-100">
            <img src="/g-data-gold-icon.png" alt="Guard" className="h-8 w-8 object-contain" />
          </div>
          <div>
            <h1 className="text-lg font-bold text-gray-900 leading-none">GuardAI by G-Data Labs</h1>
            <p className="text-xs text-gray-500 mt-1">{t("ask.tagline", "Grounded in real-time patrol data")}</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => setShowHistory(prev => !prev)}
            className={cn(
              "flex items-center gap-1.5 text-xs font-medium transition-colors px-3 py-1.5 rounded-lg border",
              showHistory
                ? "bg-blue-50 text-blue-700 border-blue-200"
                : "text-gray-500 hover:text-gray-900 border-transparent hover:bg-gray-100"
            )}
          >
            <History className="h-3.5 w-3.5" />
            {t("ask.history", "History")}
          </button>
          <button
            onClick={() => setMessages([])}
            className="hidden sm:block text-xs font-medium text-gray-500 hover:text-gray-900 transition-colors"
          >
            {t("common.clear", "Clear")}
          </button>
        </div>
      </header>

      {/* Main content row: messages + optional history sidebar */}
      <div className="flex flex-1 overflow-hidden">

      {/* Message Area */}
      <div className="flex-1 overflow-y-auto px-3 py-4 md:px-6 md:py-8 space-y-6 md:space-y-8 scroll-smooth">
        {messages.length === 0 ? (
          <div className="max-w-3xl mx-auto space-y-12 py-12">
            <div className="text-center space-y-4">
              <div className="mx-auto w-24 h-24 flex items-center justify-center transform hover:scale-105 transition-transform">
                <img src="/g-data-gold-icon.png" alt="GuardAI" className="w-full h-full object-contain" />
              </div>
              <h2 className="text-3xl font-bold text-gray-900">{t('ask.greeting')}</h2>
              <p className="text-gray-500 max-w-lg mx-auto">
                {t("ask.subtitle", "Ask about patrol compliance, missed checkpoints, guard performance, or location trends.")}
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {exampleQuestions.map((q) => (
                <button
                  key={q}
                  onClick={() => handleSuggestionClick(q)}
                  className="p-4 text-left rounded-2xl border border-gray-200 hover:border-blue-400 hover:bg-blue-50/50 transition-all group"
                >
                  <p className="text-sm font-medium text-gray-800 line-clamp-2">{q}</p>
                  <div className="mt-3 flex items-center gap-1 text-[10px] uppercase font-bold tracking-wider text-blue-600 opacity-0 group-hover:opacity-100 transition-opacity">
                    {t("ask.askNow", "Ask now")} <Send className="h-3 w-3" />
                  </div>
                </button>
              ))}
            </div>
          </div>
        ) : (
          <div className="max-w-4xl mx-auto space-y-8">
            {messages.map((msg) => (
              <div
                key={msg.id}
                className={cn(
                  "flex gap-4",
                  msg.role === 'user' ? "flex-row-reverse" : "flex-row"
                )}
              >
                {/* Avatar */}
                <div className={cn(
                  "shrink-0 w-10 h-10 rounded-xl flex items-center justify-center shadow-sm",
                  msg.role === 'user' ? "brand-blue-gradient text-white" : "bg-white border border-gray-200"
                )}>
                  {msg.role === 'user' ? <User className="h-5 w-5" /> : <img src="/g-data-gold-icon.png" className="h-7 w-7 object-contain" />}
                </div>

                {/* Message Content */}
                <div className={cn(
                  "flex flex-col gap-2 max-w-[85%]",
                  msg.role === 'user' ? "items-end" : "items-start"
                )}>
                  <div className={cn(
                    "p-4 rounded-2xl text-sm leading-relaxed",
                    msg.role === 'user'
                      ? "bg-blue-600 text-white rounded-tr-none"
                      : msg.error
                        ? "bg-amber-50 border border-amber-200 shadow-sm rounded-tl-none text-amber-900"
                        : "bg-white border border-gray-100 shadow-sm rounded-tl-none text-gray-800"
                  )}>
                    {msg.role === 'assistant' ? <AnswerText text={msg.content} /> : msg.content}
                    {msg.error && msg.detail && (
                      <details className="mt-2 text-xs text-amber-800/80">
                        <summary className="cursor-pointer select-none">{t("ask.errorDetails", "Technical details")}</summary>
                        <pre className="mt-1 whitespace-pre-wrap break-words font-mono">{msg.detail}</pre>
                      </details>
                    )}
                  </div>

                  {/* When this message was sent (question) or answered (reply).
                      Date is included once it isn't today. */}
                  <time
                    dateTime={msg.timestamp.toISOString()}
                    title={new Intl.DateTimeFormat(lang, { dateStyle: 'full', timeStyle: 'short' }).format(msg.timestamp)}
                    className="px-1 text-[11px] text-gray-400"
                  >
                    {formatMessageTime(msg.timestamp, lang)}
                  </time>

                  {msg.role === 'assistant' && msg.rows && msg.rows.length > 0 && (
                    <div className="mt-2 w-full overflow-hidden rounded-xl border border-gray-100 shadow-sm">
                      <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200 text-xs">
                          <thead className="bg-gray-50">
                            <tr>
                              {Object.keys(msg.rows[0]).map(h => (
                                <th key={h} className="px-3 py-2 text-left font-semibold text-gray-600 uppercase tracking-wider">{h}</th>
                              ))}
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-100">
                            {msg.rows.map((row, i) => (
                              <tr key={i} className="hover:bg-gray-50 transition-colors">
                                {Object.keys(row).map(h => (
                                  <td key={h} className="px-3 py-2 text-gray-700">{formatCell(row[h])}</td>
                                ))}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}

                  {msg.role === 'assistant' && msg.sql && (
                    <div>
                      <button
                        onClick={() => setShowSql(prev => ({ ...prev, [msg.id]: !prev[msg.id] }))}
                        className="flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest text-gray-400 hover:text-indigo-600 mt-1 px-1 transition-colors"
                      >
                        <FileText className="h-3 w-3" />
                        {showSql[msg.id] ? t("ask.hideSql", "Hide SQL Query") : t("ask.showSql", "Show SQL Query")}
                      </button>
                      {showSql[msg.id] && (
                        <div className="mt-2 p-3 rounded-lg bg-slate-900 text-slate-300 text-[11px] font-mono overflow-x-auto">
                          {msg.sql}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            ))}

            {mutation.isPending && (
              <div className="flex gap-4">
                <div className="shrink-0 w-10 h-10 rounded-xl bg-white border border-gray-200 flex items-center justify-center animate-pulse">
                  <img src="/g-data-gold-icon.png" className="h-7 w-7 object-contain opacity-50" />
                </div>
                <div className="bg-white border border-gray-100 shadow-sm rounded-2xl rounded-tl-none p-4 w-24">
                  <div className="flex gap-1 justify-center">
                    <div className="w-1.5 h-1.5 bg-gray-300 rounded-full animate-bounce" />
                    <div className="w-1.5 h-1.5 bg-gray-300 rounded-full animate-bounce [animation-delay:0.2s]" />
                    <div className="w-1.5 h-1.5 bg-gray-300 rounded-full animate-bounce [animation-delay:0.4s]" />
                  </div>
                </div>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>
        )}
      </div>

      {/* History Sidebar — full-screen overlay on mobile, fixed sidebar on desktop */}
      {showHistory && (
        <div className={cn(
          "border-l border-gray-200 bg-white overflow-y-auto flex flex-col",
          "absolute inset-0 z-10",
          "md:relative md:inset-auto md:z-auto md:w-80 md:bg-gray-50/50"
        )}>
          {/* Search + mobile close */}
          <div className="p-3 border-b border-gray-200 flex items-center gap-2">
            <input
              type="text"
              value={historySearch}
              onChange={(e) => setHistorySearch(e.target.value)}
              placeholder={t("ask.searchPlaceholder", "Search past queries...")}
              className="flex-1 px-3 py-2 text-xs rounded-lg border border-gray-200 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
            />
            <button
              onClick={() => setShowHistory(false)}
              className="md:hidden p-2 rounded-lg text-gray-500 hover:bg-gray-100 min-h-[44px] min-w-[44px] flex items-center justify-center"
              aria-label={t("ask.closeHistory", "Close history")}
            >
              ✕
            </button>
          </div>

          <div className="flex-1 overflow-y-auto p-4 space-y-6">
            {/* Frequently Asked (hidden when searching) */}
            {!historySearch && commonQuestions.length > 0 && (
              <div>
                <h3 className="text-[10px] font-bold uppercase tracking-widest text-gray-400 mb-3 flex items-center gap-1.5">
                  <Sparkles className="h-3 w-3" /> {t("ask.frequentlyAsked", "Frequently Asked")}
                </h3>
                <div className="space-y-2">
                  {commonQuestions.map((cq) => (
                    <button
                      key={cq.question}
                      onClick={() => { setShowHistory(false); handleSuggestionClick(cq.question); }}
                      className="w-full text-left p-3 rounded-xl bg-white border border-gray-100 hover:border-blue-300 hover:shadow-sm transition-all group"
                    >
                      <p className="text-xs font-medium text-gray-800 line-clamp-2">{cq.question}</p>
                      <p className="text-[10px] text-gray-400 mt-1.5">
                        {(cq.count === 1
                          ? t("ask.askedTimesOne", "Asked {n} time")
                          : t("ask.askedTimesOther", "Asked {n} times")
                        ).replace("{n}", new Intl.NumberFormat(lang).format(cq.count))}
                      </p>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* All Past Queries (filtered when searching) */}
            {(() => {
              const filtered = historySearch
                ? recentQueries.filter(rq => rq.question.toLowerCase().includes(historySearch.toLowerCase()))
                : recentQueries;

              if (filtered.length === 0 && historySearch) {
                return (
                  <div className="text-center py-8">
                    <p className="text-xs text-gray-400">{t("ask.noQueriesMatching", 'No queries matching "{q}"').replace("{q}", historySearch)}</p>
                  </div>
                );
              }

              // Group by date label
              const groups: { label: string; items: typeof filtered }[] = [];
              const today = new Date(); today.setHours(0,0,0,0);
              const yesterday = new Date(today); yesterday.setDate(yesterday.getDate() - 1);
              const weekAgo = new Date(today); weekAgo.setDate(weekAgo.getDate() - 7);

              for (const rq of filtered) {
                const d = new Date(rq.createdAt); d.setHours(0,0,0,0);
                let label: string;
                if (d.getTime() === today.getTime()) label = t("ask.today", "Today");
                else if (d.getTime() === yesterday.getTime()) label = t("ask.yesterday", "Yesterday");
                else if (d >= weekAgo) label = t("ask.thisWeek", "This Week");
                else label = new Intl.DateTimeFormat(lang, { month: 'long', year: 'numeric' }).format(d);

                const last = groups[groups.length - 1];
                if (last && last.label === label) {
                  last.items.push(rq);
                } else {
                  groups.push({ label, items: [rq] });
                }
              }

              return groups.map((group) => (
                <div key={group.label}>
                  <h3 className="text-[10px] font-bold uppercase tracking-widest text-gray-400 mb-3 flex items-center gap-1.5">
                    <Clock className="h-3 w-3" /> {group.label}
                  </h3>
                  <div className="space-y-2">
                    {group.items.map((rq) => (
                      <button
                        key={rq.id}
                        onClick={() => { setShowHistory(false); handleSuggestionClick(rq.question); }}
                        className="w-full text-left p-3 rounded-xl bg-white border border-gray-100 hover:border-blue-300 hover:shadow-sm transition-all group"
                      >
                        <p className="text-xs font-medium text-gray-800 line-clamp-2">{rq.question}</p>
                        <p className="text-[10px] text-gray-400 mt-1.5">
                          {t("ask.dateAtTime", "{date} at {time}")
                            .replace("{date}", new Intl.DateTimeFormat(lang, { month: 'short', day: 'numeric' }).format(new Date(rq.createdAt)))
                            .replace("{time}", new Intl.DateTimeFormat(lang, { hour: 'numeric', minute: '2-digit' }).format(new Date(rq.createdAt)))}
                          {" · "}
                          {(rq.rowCount === 1
                            ? t("ask.rowCountOne", "{n} row")
                            : t("ask.rowCountOther", "{n} rows")
                          ).replace("{n}", new Intl.NumberFormat(lang).format(rq.rowCount))}
                        </p>
                      </button>
                    ))}
                  </div>
                </div>
              ));
            })()}

            {recentQueries.length === 0 && commonQuestions.length === 0 && !historyLoading && (
              <div className="text-center py-8">
                <History className="h-8 w-8 text-gray-300 mx-auto mb-2" />
                <p className="text-xs text-gray-400">{t("ask.noHistory", "No history yet. Ask a question to get started.")}</p>
              </div>
            )}
          </div>
        </div>
      )}

      </div>{/* end main content row */}

      {/* Input Area */}
      <footer className="p-3 md:p-6 bg-white border-t border-gray-100">
        <form onSubmit={handleSubmit} className="max-w-4xl mx-auto relative">
          <textarea
            value={question}
            onChange={(e) => setQuestion(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                handleSubmit();
              }
            }}
            placeholder={t("ask.inputPlaceholder", "Ask something like “Which locations had the fewest scans yesterday?”")}
            rows={1}
            className="w-full pl-6 pr-14 py-4 rounded-2xl border border-gray-200 bg-gray-50 focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 text-sm transition-all resize-none shadow-inner"
            style={{ minHeight: '56px', maxHeight: '200px' }}
          />
          <button
            type="submit"
            disabled={!question.trim() || mutation.isPending}
            className={cn(
              "absolute right-2.5 top-2.5 p-2 rounded-xl transition-all shadow-sm",
              !question.trim() || mutation.isPending
                ? "bg-gray-100 text-gray-400"
                : "brand-blue-gradient text-white hover:scale-105 active:scale-95"
            )}
          >
            {mutation.isPending ? <Loader2 className="h-5 w-5 animate-spin" /> : <Send className="h-5 w-5" />}
          </button>
          <div className="mt-3 text-center">
            <p className="text-[10px] text-gray-400 uppercase tracking-widest font-semibold flex items-center justify-center gap-2">
              <ShieldCheck className="h-3 w-3" /> {t("ask.encryptedPrivate", "Encrypted & Private Analysis")}
            </p>
          </div>
        </form>
      </footer>
    </div>
  );
}

function formatCell(value: unknown) {
  if (value === null || value === undefined) return "—";
  if (typeof value === "object") {
    return JSON.stringify(value);
  }
  return String(value);
}

// Dummy icon for footer
function ShieldCheck({ className }: { className?: string }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="24" height="24"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
    >
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10" />
      <path d="m9 12 2 2 4-4" />
    </svg>
  );
}
